/**
 * Moore-neighbour boundary tracing with Jacob's stopping criterion.
 *
 * The tracer walks the 8-neighbourhood of the current boundary pixel starting
 * from the last background pixel it saw ("the backtrack") and turning
 * clockwise, which keeps the region on a consistent side all the way round.
 *
 * ## The stopping criterion is the whole difficulty
 *
 * The naive rule — stop as soon as the start pixel comes up again — truncates
 * any contour that legitimately passes through its start pixel more than once,
 * which is every one-pixel-wide spur and every piece pinched to a point.
 *
 * Jacob's criterion fixes that: stop when the start pixel is entered a *second
 * time in the same direction* as the first entry. Implemented literally against
 * an "initial entry direction" it does not terminate at all — the seed is never
 * entered, it is where the walk began, so on a one-pixel-wide sliver (traced
 * out along the row and back along the same row) no arrival ever matches the
 * notional initial direction and the walk spins forever.
 *
 * The formulation that does terminate, and is what "same direction" means
 * operationally, is that the tracer's *state* repeats: arriving at the start
 * pixel with the walk about to step onto the same second pixel as the first
 * time round means the loop has closed. That is one look-ahead step, and it
 * handles the rectangle (returns from the south, still closes), the sliver
 * (returns from the east, still closes) and the pinch point (passes through
 * mid-contour, correctly does *not* close) with the same test.
 */

import { NEIGHBOR_DX, NEIGHBOR_DY } from './components'
import type { Point } from './geometry'

/**
 * Direction index of an offset, keyed by `(dy + 1) * 3 + (dx + 1)`.
 * `-1` marks the zero offset, which never occurs here.
 */
const DIRECTION_OF_OFFSET = [5, 6, 7, 4, -1, 0, 3, 2, 1] as const

/** The tracer starts having "come from the west" — see {@link traceBoundary}. */
const INITIAL_BACKTRACK = 4

/** One clockwise step of the walk: where it lands and what the next backtrack is. */
interface Step {
  x: number
  y: number
  backtrack: number
}

/**
 * Finds the next boundary pixel clockwise from the backtrack, or `null` when
 * the component has no neighbour at all (a single isolated pixel).
 */
function nextStep(
  labels: Int32Array,
  width: number,
  height: number,
  id: number,
  x: number,
  y: number,
  backtrack: number,
): Step | null {
  for (let k = 1; k <= 8; k += 1) {
    const dir = (backtrack + k) & 7
    const nx = x + NEIGHBOR_DX[dir]!
    const ny = y + NEIGHBOR_DY[dir]!
    if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
    if (labels[ny * width + nx] !== id) continue

    // The neighbour examined immediately before this one is the background
    // pixel the walk is stepping off; expressed relative to the pixel it moves
    // to, that becomes the next backtrack. The two are always 8-adjacent
    // (consecutive offsets differ by exactly one unit step), so the lookup is
    // always defined.
    const prevDir = (backtrack + k - 1) & 7
    const bx = x + NEIGHBOR_DX[prevDir]!
    const by = y + NEIGHBOR_DY[prevDir]!
    return { x: nx, y: ny, backtrack: DIRECTION_OF_OFFSET[(by - ny + 1) * 3 + (bx - nx + 1)]! }
  }
  return null
}

/**
 * Traces the outer boundary of one labelled component and returns its ring of
 * pixel centres, without a repeated closing vertex.
 *
 * `seed` must be the component's first pixel in raster order (what
 * `labelComponents` records), because the walk relies on the seed's west
 * neighbour lying outside the component to set the initial backtrack.
 *
 * A component of a single isolated pixel returns a one-point ring; callers get
 * to decide whether that is noise or a defect (it is reported as
 * `invalid_ring`).
 */
export function traceBoundary(
  labels: Int32Array,
  width: number,
  height: number,
  id: number,
  seed: number,
): Point[] {
  const startX = seed % width
  const startY = (seed - startX) / width

  const ring: Point[] = [[startX, startY]]

  const first = nextStep(labels, width, height, id, startX, startY, INITIAL_BACKTRACK)
  if (!first) return ring
  ring.push([first.x, first.y])

  let current = first

  // Hard bound on the walk. A boundary may revisit pixels (a thin spur is
  // traced out and back), so the bound is generous; it exists only so that a
  // mask this code has not anticipated degrades into a truncated contour
  // instead of hanging the browser tab.
  const limit = 8 * width * height + 8

  for (let step = 0; step < limit; step += 1) {
    const next = nextStep(labels, width, height, id, current.x, current.y, current.backtrack)
    if (!next) return ring

    if (next.x === startX && next.y === startY) {
      const lookahead = nextStep(labels, width, height, id, startX, startY, next.backtrack)
      if (lookahead && lookahead.x === first.x && lookahead.y === first.y) return ring
    }

    ring.push([next.x, next.y])
    current = next
  }

  return ring
}
